import React from 'react';
import {hydrate, render} from 'react-dom';
import {Provider} from 'react-redux';
import AbacusClientUtils from '@homeaway/abacus-client-utils';
import {configureStore} from '../../shared/startup/configureStore';
import {withErrorBoundary} from '../../shared/components/common/error-boundary/ErrorBoundary';
import LazyLoad from '../../shared/components/common/lazy-image/react-lazyload';
import {LAZY_LOAD_OFFSET} from '../../shared/utils/constants';
import {SearchBoxLite} from '../../shared/components/common/searchboxlite/SearchBoxLite';
import {TESTS} from '../../shared/utils/abacus';

import {ApolloClient} from 'apollo-client';
import {InMemoryCache} from 'apollo-cache-inmemory';
import {HttpLink} from 'apollo-link-http';
import {ApolloProvider} from 'react-apollo';

const {DEFAULT: LAZY_LOAD_OFFSET_DEFAULT} = LAZY_LOAD_OFFSET;

/* eslint consistent-return:0 */
export default function hydrateApp() {
    // Grab the state from a global injected into server-generated HTML
    const preloadedState = window.__PRELOADED_STATE__;
    const {
        config: {
            template,
            url: {
                basePathPrefix
            }
        }
    } = preloadedState;

    // Create Redux store with initial state
    const store = configureStore({
        initialState: preloadedState
    });

    const {abacus} = store.getState();

    AbacusClientUtils.initialize(abacus);
    if (abacus[TESTS.POLYFILL_SERVICE]) {
        AbacusClientUtils.logExperiment(abacus[TESTS.POLYFILL_SERVICE]);
    }

    const cache = new InMemoryCache().restore();

    const client = new ApolloClient({
        ssrMode: true,
        cache,
        link: new HttpLink({
            uri: `${basePathPrefix}/graphql`,
            credentials: 'same-origin',
            headers: {
                'client-name': 'landingapp',
                'client-version': '1.0.0',
            },
        })
    });

    hydrate(
        <Provider store={store}>
            {withErrorBoundary(<SearchBoxLite />)}
        </Provider>,
        document.querySelector('.searchbox-root')
    );

    import(
        /* webpackChunkName: "site-header" */
        /* webpackPreload: true */
        '../../shared/components/common/site-header/SiteHeader'
    ).then((module) => {
        const SiteHeader = module.default;
        render(
            <Provider store={store}>
                {withErrorBoundary(
                    <SiteHeader />
                )}
            </Provider>,
            document.querySelector('.site-header-root')
        );
    });

    if (process.env.NODE_ENV === 'development') {
        import(
            /* webpackChunkName: "content-[request]" */
            /* webpackPreload: true */
            `../../shared/templates/${template}.js`
        ).then(({Template}) => {
            hydrate(
                <ApolloProvider client={client}>
                    <Provider store={store}>
                        <Template />
                    </Provider>
                </ApolloProvider>,
                document.querySelector('.content-root')
            );
        });
    } else {
        import(
            /* webpackChunkName: "content" */
            /* webpackPreload: true */
            '../../shared/App'
        ).then(({App}) => {
            hydrate(
                <ApolloProvider client={client}>
                    <Provider store={store}>
                        <App />
                    </Provider>
                </ApolloProvider>,
                document.querySelector('.content-root')
            );
        });
    }

    import(
        /* webpackChunkName: "site-footer" */
        '../../shared/components/common/site-footer/AsyncSiteFooter'
    ).then(({AsyncSiteFooter}) => {
        render(
            <Provider store={store}>
                <LazyLoad once offset={LAZY_LOAD_OFFSET_DEFAULT}>
                    {withErrorBoundary(<AsyncSiteFooter />)}
                </LazyLoad>
            </Provider>,
            document.querySelector('.site-footer-root')
        );
    });
}
